import React from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import SupportOutlinedIcon from '@mui/icons-material/SupportOutlined'
import { getResourceIcon } from '../../../utils/resourceIcons'
import type { FranchisePartnerResource } from '../../../types/resources'
import { getSupportPortalLinks } from '../../../config/supportPortals'
import { poolwerxColors } from '../../../theme/colors'

interface RailTileProps {
  icon: React.ReactNode
  label: string
  subtitle?: string
  href?: string
  accentColor?: string
  disabled?: boolean
}

const RailTile: React.FC<RailTileProps> = ({
  icon,
  label,
  subtitle,
  href,
  accentColor,
  disabled = false,
}) => {
  const commonStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 1.25,
    py: 1,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'grey.200',
    textDecoration: 'none',
    color: disabled ? 'text.disabled' : 'text.primary',
    bgcolor: disabled ? 'grey.50' : 'background.paper',
    transition: 'all 0.15s',
    cursor: disabled ? 'not-allowed' : 'pointer',
    ...(disabled
      ? {}
      : {
          '&:hover': {
            borderColor: accentColor ?? 'primary.main',
            bgcolor: 'grey.50',
            '& .launch-icon': {
              color: accentColor ?? 'primary.main',
              transform: 'translate(2px, -2px)',
            },
          },
        }),
  }

  const contents = (
    <>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          opacity: disabled ? 0.4 : 1,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              display: 'block',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {!disabled && (
        <LaunchIcon
          className="launch-icon"
          fontSize="small"
          sx={{
            color: 'text.disabled',
            transition: 'all 0.15s',
            flexShrink: 0,
          }}
        />
      )}
    </>
  )

  if (disabled || !href) {
    return <Box sx={commonStyles}>{contents}</Box>
  }

  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={commonStyles}
    >
      {contents}
    </Box>
  )
}

interface RailSectionProps {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
}

const RailSection: React.FC<RailSectionProps> = ({ title, icon, children }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      {icon}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.8, fontWeight: 600 }}
      >
        {title}
      </Typography>
    </Stack>
    <Stack spacing={0.75}>{children}</Stack>
  </Box>
)

interface QuickAccessRailProps {
  country?: string
  resources?: FranchisePartnerResource[]
}

export const QuickAccessRail: React.FC<QuickAccessRailProps> = ({
  country,
  resources,
}) => {
  const supportLinks = getSupportPortalLinks(country)

  // Hide customer_intelligence entirely when not configured to keep the
  // rail tight; other resources show as "Not configured" so they can be
  // populated from the admin form.
  const quickLinks = (resources ?? []).filter(
    r => !(r.resource_code === 'customer_intelligence' && !r.url),
  )

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderColor: 'grey.200',
        position: { xs: 'static', md: 'sticky' },
        top: { md: 24 },
      }}
    >
      <Stack spacing={2.5}>
        {quickLinks.length > 0 && (
          <RailSection
            title="Quick Links"
            icon={<LinkOutlinedIcon sx={{ fontSize: 18, color: poolwerxColors.teal }} />}
          >
            {quickLinks.map(resource => {
              const Icon = getResourceIcon(resource.resource_type)
              const url = resource.url
              return (
                <RailTile
                  key={resource.resource_code}
                  icon={<Icon size={22} />}
                  label={resource.label}
                  subtitle={url ? undefined : 'Not configured'}
                  href={url ?? undefined}
                  disabled={!url}
                />
              )
            })}
          </RailSection>
        )}

        <RailSection
          title="Support"
          icon={<SupportOutlinedIcon sx={{ fontSize: 18, color: poolwerxColors.teal }} />}
        >
          {supportLinks.map((link) => {
            const Icon = link.Icon
            return (
              <RailTile
                key={link.key}
                icon={<Icon sx={{ color: poolwerxColors.teal, fontSize: 22 }} />}
                label={link.label}
                subtitle={link.helper}
                href={link.url}
                accentColor={poolwerxColors.teal}
              />
            )
          })}
        </RailSection>
      </Stack>
    </Paper>
  )
}

export default QuickAccessRail
